import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Brain, Compass, ShieldCheck, CreditCard, Mail, CheckCircle2, FileText, GraduationCap } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import { LANDING } from "@/constants/testIds";

const HERO = "https://images.unsplash.com/photo-1534644107580-3a4dbd494a95?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const steps = [
  { n: "01", t: "Subscribe", d: "Choose an individual or institutional plan and complete a secure payment.", icon: CreditCard },
  { n: "02", t: "Receive Test ID", d: "We email a unique, single-use Test ID locked to your email.", icon: Mail },
  { n: "03", t: "Verify Email", d: "Enter your Test ID and verify your email via a 6-digit OTP.", icon: ShieldCheck },
  { n: "04", t: "Take the Test", d: "Pick MSIS or MSOS, choose your domains, and complete the assessment.", icon: Brain },
  { n: "05", t: "Get Your Report", d: "A detailed report is shown on-screen and emailed to you.", icon: FileText },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50">
      <SiteNav />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="stagger">
          <p className="eyebrow">Management Specialization Assessments</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-3 leading-[1.05]">
            Discover the management specialization that fits you best.
          </h1>
          <p className="text-slate-500 mt-5 text-lg max-w-xl">
            Two scientifically grounded assessments across the same <b className="text-slate-700">9 specialization domains</b> — choose with evidence, not peer pressure.
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-5 text-sm">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-700 font-medium">MSIS · Inclination Scale</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-600 text-white font-medium">MSOS · Orientation Scale</span>
            <span className="text-slate-400">Two offerings, one platform.</span>
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/take-test">
              <Button data-testid={LANDING.takeTest} className="h-12 px-7 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-base">
                Take the Test <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button data-testid={LANDING.pricing} variant="outline" className="h-12 px-7 rounded-full text-base">Buy the Test</Button>
            </Link>
          </div>
          <div className="flex gap-8 mt-10">
            <div><div className="text-3xl font-bold">9</div><div className="text-xs text-slate-500 uppercase tracking-wider">Domains</div></div>
            <div><div className="text-3xl font-bold">2</div><div className="text-xs text-slate-500 uppercase tracking-wider">Assessments</div></div>
            <div><div className="text-3xl font-bold">1</div><div className="text-xs text-slate-500 uppercase tracking-wider">Test ID · one use</div></div>
          </div>
        </div>
        <div className="relative">
          <img src={HERO} alt="Student taking an assessment" className="rounded-2xl w-full h-[420px] object-cover border border-slate-200" />
        </div>
      </section>

      {/* Two tests */}
      <section className="max-w-6xl mx-auto px-5 py-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-8 rounded-xl bg-white border border-slate-200">
            <div className="w-12 h-12 rounded-lg bg-slate-100 grid place-items-center mb-5"><Brain className="w-6 h-6" /></div>
            <h2 className="text-2xl font-bold">MSIS</h2>
            <p className="eyebrow mt-1">Inclination Scale</p>
            <p className="text-slate-500 mt-4">A psychometric measure of your attitudes, personality and preferences — revealing which specialization you are <i>inclined</i> toward, and why.</p>
            <Link to="/take-test" className="inline-flex items-center gap-1 mt-6 font-semibold text-slate-900">Take MSIS <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="p-8 rounded-xl bg-slate-900 text-white border border-slate-900 relative overflow-hidden">
            <span className="absolute top-6 right-6 text-[10px] tracking-widest uppercase bg-blue-600 px-2 py-1 rounded-full">New</span>
            <div className="w-12 h-12 rounded-lg bg-white/10 grid place-items-center mb-5"><Compass className="w-6 h-6 text-blue-300" /></div>
            <h2 className="text-2xl font-bold">MSOS</h2>
            <p className="eyebrow mt-1 text-blue-300">Orientation Scale</p>
            <p className="text-slate-300 mt-4">A general-intelligence reasoning assessment (right/wrong scored) that indicates which domain your cognitive cues <i>orient</i> toward — your recommended specialization.</p>
            <Link to="/take-test" className="inline-flex items-center gap-1 mt-6 font-semibold text-white">Take MSOS <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <p className="eyebrow">How it works</p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2 max-w-2xl">Five steps from sign-up to insight.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-10">
          {steps.map((s) => (
            <div key={s.n} className="p-6 rounded-xl bg-white border border-slate-200 hover:-translate-y-1 transition-transform">
              <div className="flex items-center justify-between">
                <s.icon className="w-6 h-6 text-blue-700" />
                <span className="text-xs font-bold text-slate-300">{s.n}</span>
              </div>
              <h3 className="font-semibold mt-4">{s.t}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-snug">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Institutions band */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-5 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="flex items-center gap-2 text-blue-300"><GraduationCap className="w-5 h-5" /><span className="eyebrow text-blue-300">For Institutions</span></div>
            <h2 className="text-3xl font-bold tracking-tight mt-3">Run MSIS or MSOS for an entire batch.</h2>
            <p className="text-slate-400 mt-4 max-w-md">Generate single-use Test IDs in bulk with slab-based pricing. Every Test ID works for either test, once.</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/institutions"><Button className="rounded-full bg-white text-slate-900 hover:bg-slate-100">Institution dashboard</Button></Link>
              <Link to="/pricing"><Button variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10">See slab pricing</Button></Link>
            </div>
          </div>
          <ul className="space-y-3">
            {["Bulk Test ID generation", "Test IDs work for MSIS or MSOS", "Detailed per-student reports", "Admin-managed pricing & domains"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-slate-200"><CheckCircle2 className="w-5 h-5 text-blue-400" /> {t}</li>
            ))}
          </ul>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 py-8 text-sm text-slate-400 flex items-center justify-between">
          <span>© {new Date().getFullYear()} MSI Scale</span>
          <div className="flex gap-6">
            <Link to="/pricing" className="hover:text-slate-700">Pricing</Link>
            <Link to="/take-test" className="hover:text-slate-700">Take the Test</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
